import React, { useState, useEffect, useMemo } from 'react';
import { AlertCircle, Activity, Trophy, Calendar, DollarSign, Phone, PhoneCall, Users, Percent, MessageSquare } from 'lucide-react';
import { useCallAgentLeads, useCallLogs, useMeetingsBooked } from '../../hooks/useSupabaseLeads';
import { usePipelineDeals } from '../../hooks/usePipelineDeals';
import AgentHeader from './AgentHeader';
import AgentActionPanel from './AgentActionPanel';
import TodayHighlights from './TodayHighlights';
import ActivityFeed from './ActivityFeed';
import PerformanceIntelligence from '../shared/PerformanceIntelligence';
import AgentMilestones from '../shared/AgentMilestones';
import type { MilestoneTier } from '../shared/AgentMilestones';
import NextActionsPanel from './NextActionsPanel';
import TestAgentModal from './TestAgentModal';
import HowIWorkModal from './HowIWorkModal';
import FeedbackPopup from '../FeedbackPopup';
import LoadingScreen from '../shared/LoadingScreen';
import { getAgentSettingsRow } from '../../services/settingsService';
import { useTasksQueue } from '../../hooks/useTasksQueue';
import { isConnectedOutcome } from '../../utils/callOutcomes';

const CallAgentPage = () => {
  const { data: allCallLeads, isLoading: leadsLoading, error: leadsError } = useCallAgentLeads();
  const { data: allCallLogs, isLoading: logsLoading, error: logsError } = useCallLogs();
  const taskQueue = useTasksQueue('callagent');
  const { data: pipelineDeals } = usePipelineDeals();
  const { data: allMeetings } = useMeetingsBooked();
  const [showTestAgentModal, setShowTestAgentModal] = useState(false);
  const [showHowIWorkModal, setShowHowIWorkModal] = useState(false);
  const [agentAvatarUrl, setAgentAvatarUrl] = useState<string | undefined>(undefined);

  useEffect(() => {
    getAgentSettingsRow('call').then(row => {
      if (row?.avatar_url) setAgentAvatarUrl(row.avatar_url);
    });
  }, []);

  const isLoading = leadsLoading || logsLoading;
  const error = leadsError || logsError;

  const todayStart = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  }, []);

  const todayEnd = useMemo(() => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    return today;
  }, []);

  const todayCallLogs = useMemo(() => {
    return allCallLogs.filter(log => {
      const logDate = new Date(log.created_at);
      return logDate >= todayStart && logDate <= todayEnd;
    });
  }, [allCallLogs, todayStart, todayEnd]);

  const recentCallLogs = useMemo(() => {
    return allCallLogs.slice(0, 50);
  }, [allCallLogs]);

  const todayStats = useMemo(() => {
    const leadsContacted = new Set(todayCallLogs.map(log => log.lead_id).filter(Boolean)).size;
    const callsMade = todayCallLogs.length;
    const callsAnswered = todayCallLogs.filter(log => isConnectedOutcome(log.call_outcome)).length;
    const answerRate = callsMade > 0 ? Math.round((callsAnswered / callsMade) * 100) : 0;
    const positiveConversations = todayCallLogs.filter(log => {
      const outcome = log.call_outcome?.toLowerCase() || '';
      return outcome.includes('call later') || outcome.includes('info requested') || outcome.includes('booked');
    }).length;
    const todayMeetings = allMeetings.filter(m => {
      const d = new Date(m.created_at);
      const channel = (m.booked_channel || '').toLowerCase();
      return d >= todayStart && d <= todayEnd && (channel.includes('call'));
    }).length;

    return { leadsContacted, callsMade, callsAnswered, answerRate, positiveConversations, meetingsBooked: todayMeetings };
  }, [todayCallLogs, allMeetings, todayStart, todayEnd]);

  const agentStatus = useMemo(() => {
    if (allCallLogs.length === 0) return 'idle';

    const lastCall = allCallLogs[0];
    const lastCallTime = new Date(lastCall.created_at);
    const now = new Date();
    const hoursSinceLastCall = (now.getTime() - lastCallTime.getTime()) / (1000 * 60 * 60);

    if (hoursSinceLastCall < 1) return 'active';
    if (allCallLeads.some(lead => lead.ca_next_call_timestamp && new Date(lead.ca_next_call_timestamp) > now)) return 'idle';
    return 'processing';
  }, [allCallLogs, allCallLeads]);

  const meetingsBooked = useMemo(() => {
    return allMeetings.filter(m => {
      const channel = (m.booked_channel || '').toLowerCase();
      return channel.includes('call');
    }).length;
  }, [allMeetings]);

  const pipelineValue = useMemo(() => {
    return pipelineDeals
      .filter(d => (d.channel_booked || '').toLowerCase().includes('call'))
      .reduce((sum, d) => sum + (d.deal_value || 0), 0);
  }, [pipelineDeals]);

  const milestoneTiers: MilestoneTier[] = useMemo(() => [
    {
      icon: Trophy,
      title: '1,000 Calls Done',
      target: 1000,
      reward: 'Get 200 Free Call Credits',
      currentValue: allCallLogs.length,
      formatValue: (c, t) => `${c.toLocaleString()} / ${t.toLocaleString()} calls`,
    },
    {
      icon: Calendar,
      title: '10 Meetings Booked',
      target: 10,
      reward: '500 Free Call Credits',
      currentValue: meetingsBooked,
      formatValue: (c, t) => `${c} / ${t} meetings booked`,
    },
    {
      icon: DollarSign,
      title: '$150,000 Pipeline Generated',
      target: 150000,
      reward: '1,000 Free Call Credits',
      currentValue: pipelineValue,
      formatValue: (c, t) => `$${c.toLocaleString()} / $${t.toLocaleString()}`,
    },
  ], [allCallLogs.length, meetingsBooked, pipelineValue]);

  if (isLoading && allCallLeads.length === 0 && allCallLogs.length === 0) {
    return <LoadingScreen />;
  }

  return (
    <div>
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 p-3 rounded-lg flex items-center space-x-2 mb-6">
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      <AgentHeader
        status={agentStatus}
        title="Call Agent"
        subtitle="I call leads, qualify them, and schedule meetings."
        avatarSrc={agentAvatarUrl}
        avatarAlt="Call Agent"
        activeLabel="Active Now"
        icon="phone"
        processingMessages={[
          'Analyzing call patterns',
          'Preparing summaries',
          'Optimizing call scripts',
          'Reviewing lead data',
          'Planning next actions',
          'Updating insights',
          'Processing feedback',
          'Calibrating strategies'
        ]}
      />

      <AgentActionPanel
        onTestCall={() => setShowTestAgentModal(true)}
        onHowIWork={() => setShowHowIWorkModal(true)}
        campaignPath="/campaigns/call"
        settingsPath="call-agent"
        howIWorkDescription="See my calling strategy, behavior rules & decision logic."
        viewCampaignDescription="See the steps I follow and your leads assigned to me."
        testMeDescription="Enter your phone number — I'll call you in a few seconds."
        customizeMeDescription="Adjust my persona, scripts, and behavior rules."
      />

      <div className="mt-[60px]">
        <TodayHighlights
          metrics={[
            { value: todayStats.leadsContacted, label: 'Leads Contacted', icon: Users },
            { value: todayStats.callsMade, label: 'Calls Made', icon: Phone },
            { value: todayStats.callsAnswered, label: 'Calls Answered', icon: PhoneCall },
            { value: todayStats.answerRate, label: 'Answer Rate', icon: Percent, suffix: '%' },
            { value: todayStats.positiveConversations, label: 'Positive Conversations', icon: MessageSquare },
            { value: todayStats.meetingsBooked, label: 'Meetings Booked', icon: Calendar },
          ]}
        />
      </div>

      <div className="space-y-3 mt-[60px]">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-br from-[#1875FF] to-[#05E0D1] rounded-xl flex items-center justify-center shadow-lg">
            <Activity className="text-white" size={22} />
          </div>
          <h2 className="text-2xl font-extrabold text-gray-900 tracking-tight font-display">Live Operations</h2>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" style={{ minHeight: '600px' }}>
          <ActivityFeed
            processingTasks={taskQueue.processingTasks}
            recentFinishedTasks={taskQueue.recentFinishedTasks}
            hasProcessing={taskQueue.hasProcessing}
            hasAnyTasks={taskQueue.hasAnyTasks}
            isLoading={taskQueue.isLoading}
            emptyState={{ icon: Phone, title: 'No calls in queue', subtitle: 'Assign leads to start calling' }}
          />
          <NextActionsPanel
            pendingTasks={taskQueue.pendingTasks}
            hasAnyTasks={taskQueue.hasAnyTasks}
            isLoading={taskQueue.isLoading}
            emptyState={{ icon: Phone, title: 'No calls scheduled', subtitle: 'Upcoming calls will appear here' }}
          />
        </div>
      </div>

      <div className="mt-[60px]">
        <PerformanceIntelligence agentType="call" />
      </div>

      <div className="mt-[60px]">
        <AgentMilestones tiers={milestoneTiers} />
      </div>

      <TestAgentModal
        isOpen={showTestAgentModal}
        onClose={() => setShowTestAgentModal(false)}
        agentType="call"
      />

      <HowIWorkModal
        isOpen={showHowIWorkModal}
        onClose={() => setShowHowIWorkModal(false)}
      />

      <FeedbackPopup isVisible={true} />
    </div>
  );
};

export default CallAgentPage;
